import { store } from 'redux/store';
import { useCallback, useEffect, useState } from 'react';

import { Range } from 'types';

import { getPartnerAvailableRanges } from 'modules/partners/api/getPartnerAvailableRanges';

import { removeRangeFromRanges } from 'utils/removeRangeFromRanges';
import { removeRangeFromRange } from 'utils/removeRangeFromRange';
import { checkIsValidRange } from '../utils/checkIsValidRange';

type UseGetAvailableRangesProps = {
  partnerId: string;
  holdingClassId: string;
  page: number;
  rowsPerPage: number;
};

export const useGetAvailableRanges = ({
  partnerId,
  holdingClassId,
  page = 1,
  rowsPerPage = 10,
}: UseGetAvailableRangesProps) => {
  const [availableRanges, setAvailableRanges] = useState<Range[]>([]);
  const [fetchedRanges, setFetchedRanges] = useState<Range[]>([]);
  const [fetchedTotalRanges, setFetchedTotalRanges] = useState<number>(0);
  const [validatedRanges, setValidatedRanges] = useState<Range[]>([]);
  const [generatedRanges, setGeneratedRanges] = useState<Range[]>([]);
  const [removedRanges, setRemovedRanges] = useState<Range[]>([]);
  const [totalAvailableRanges, setTotalAvailableRanges] = useState<number>(0);

  const [addedRangesByPartner, setAddedRangesByPartner] = useState<
    Record<string, Range[]>
  >({});
  const [isValidRange, setIsValidRange] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const updateRemovedAndGeneratedRanges = (
    resultRange: Range,
    range: Range
  ) => {
    if (resultRange && resultRange.from !== -1) {
      setRemovedRanges((prev) => {
        if (
          prev.some((r) => r.from <= resultRange.from && r.to >= resultRange.to)
        ) {
          return prev;
        }
        return [...prev, resultRange];
      });

      setGeneratedRanges((prev) =>
        prev.filter(
          (r) => !(r.from <= resultRange.from && r.to >= resultRange.to)
        )
      );
    }

    const generatedRanges = removeRangeFromRange(range, resultRange);
    setGeneratedRanges((prev) => [...prev, ...generatedRanges]);
  };

  const handleValidateRange = async (range: Range): Promise<boolean> => {
    try {
      const resultRange = await checkIsValidRange({
        range,
        availableRanges,
        addedRanges: [
          ...(addedRangesByPartner?.[partnerId] || []),
          ...validatedRanges,
        ],
      });

      if (!resultRange || resultRange.from === -1) {
        setIsValidRange(false);
        return false;
      }

      setIsValidRange(true);
      setValidatedRanges((prev) => [...prev, range]);

      const newAvailableRanges = removeRangeFromRanges(range, availableRanges);
      setAvailableRanges(newAvailableRanges);

      updateRemovedAndGeneratedRanges(resultRange, range);

      return true;
    } catch (error) {
      setIsValidRange(false);
      return false;
    }
  };

  const handleResetValues = () => {
    setValidatedRanges([]);
    setGeneratedRanges([]);
    setRemovedRanges([]);
    setIsValidRange(true);
  };

  const handleSaveMovement = () => {
    setAddedRangesByPartner((prev) => {
      const updatedRanges = [...(prev[partnerId] || []), ...validatedRanges];
      return { ...prev, [partnerId]: updatedRanges };
    });

    handleResetValues();
  };

  useEffect(() => {
    const partnerAddedRanges = addedRangesByPartner?.[partnerId] || [];

    const partnerRanges = [
      ...(partnerAddedRanges.filter(
        (range: Range) => range.shareClass === holdingClassId
      ) || []),
      ...validatedRanges,
    ];

    let availableRanges: Range[] = fetchedRanges || [];

    partnerRanges.forEach((range: Range) => {
      availableRanges = removeRangeFromRanges(range, availableRanges);
    });
    setAvailableRanges(availableRanges);

    const generatedRangesCount = availableRanges.length - fetchedRanges?.length;
    setTotalAvailableRanges(fetchedTotalRanges + generatedRangesCount);
  }, [
    partnerId,
    addedRangesByPartner,
    validatedRanges,
    fetchedRanges,
    fetchedTotalRanges,
    holdingClassId,
  ]);

  const fetchRanges = useCallback(async () => {
    if (!partnerId || !holdingClassId) return;
    try {
      setIsLoading(true);

      const rangesData = await store.dispatch(
        getPartnerAvailableRanges({
          partnerId,
          holdingClassId,
          params: { page, itemsPerPage: rowsPerPage },
        })
      );

      const { ranges, totalRanges } = rangesData;

      setFetchedRanges(ranges);
      setFetchedTotalRanges(totalRanges);

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  }, [partnerId, holdingClassId, page, rowsPerPage]);

  useEffect(() => {
    fetchRanges();
  }, [fetchRanges]);

  return {
    data: {
      validatedRanges,
      availableRanges,
      generatedRanges,
      removedRanges,
      totalAvailableRanges,
      isValidRange,
    },
    isLoading,
    actions: {
      handleValidateRange,
      handleResetValues,
      handleSaveMovement,
    },
  };
};
